import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Card, PortalPage, Spinner } from "@/components/PortalPage";
import { NotEnabled, useBillingStatus } from "@/pages/billing/BillingHome";
import { PracticeBanner } from "@/components/RoyaltyTerms";
import { ScheduleDialog, fmtTime, parseLocal, techColor, toLocal, ymd } from "@/pages/billing/Schedule";

const HOURS = Array.from({ length: 15 }, (_, i) => i + 6);
const DAY = 864e5;
const startOfWeek = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - x.getDay()); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const range = (view, cursor) => {
  if (view === "day") { const s = new Date(cursor); s.setHours(0, 0, 0, 0); return [s, s]; }
  if (view === "week") { const s = startOfWeek(cursor); return [s, addDays(s, 6)]; }
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const s = startOfWeek(first);
  return [s, addDays(s, 41)];
};

const onDay = (j, day) => {
  const s = j.start_at.slice(0, 10), e = (j.end_at || j.start_at).slice(0, 10);
  return s <= day && day <= e;
};

const Chip = ({ j, techs, onOpen, compact }) => (
  <div draggable onDragStart={(e) => e.dataTransfer.setData("job", j.id)} onClick={(e) => { e.stopPropagation(); onOpen(j); }} data-testid={`cal-job-${j.id}`}
    className="cursor-pointer truncate rounded px-1.5 py-0.5 text-[11px] font-bold text-black transition-transform hover:scale-[1.02]"
    style={{ background: j.technician_ids?.length ? techColor(techs, j.technician_ids[0]) : "#9A9A9A", opacity: j.status === "completed" ? 0.55 : 1 }}>
    {compact ? "" : `${fmtTime(j.start_at)} `}{j.customer_name} · {j.title}
  </div>
);

const MonthGrid = ({ days, cursor, jobs, techs, onOpen, onDrop }) => (
  <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-[#262626] bg-[#262626]" data-testid="cal-month">
    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="bg-[#141414] p-1.5 text-center text-[10px] font-bold tracking-[2px] text-[#777]">{d.toUpperCase()}</div>)}
    {days.map((d) => {
      const k = ymd(d);
      const list = jobs.filter((j) => onDay(j, k));
      return (
        <div key={k} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e.dataTransfer.getData("job"), k)} data-testid={`cal-day-${k}`}
          className={`min-h-[96px] space-y-1 bg-[#111] p-1.5 ${d.getMonth() !== cursor.getMonth() ? "opacity-40" : ""} ${k === ymd(new Date()) ? "ring-1 ring-inset ring-[var(--accent)]" : ""}`}>
          <p className="text-[11px] text-[#888]">{d.getDate()}</p>
          {list.slice(0, 4).map((j) => <Chip key={j.id} j={j} techs={techs} onOpen={onOpen} />)}
          {list.length > 4 && <p className="text-[10px] text-[#888]">+{list.length - 4} more</p>}
        </div>
      );
    })}
  </div>
);

const TimeGrid = ({ days, jobs, techs, onOpen, onDrop }) => (
  <div className="overflow-x-auto rounded-lg border border-[#262626]" data-testid="cal-timegrid">
    <div className="grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(${days.length > 1 ? 110 : 200}px, 1fr))` }}>
      <div className="bg-[#141414]" />
      {days.map((d) => <div key={ymd(d)} className={`bg-[#141414] p-2 text-center text-xs font-bold ${ymd(d) === ymd(new Date()) ? "text-[var(--accent)]" : "text-[#ccc]"}`}>{d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</div>)}
      <div>{HOURS.map((h) => <div key={h} className="h-12 border-t border-[#1f1f1f] pr-1 text-right text-[10px] text-[#666]">{new Date(2000, 0, 1, h).toLocaleTimeString([], { hour: "numeric" })}</div>)}</div>
      {days.map((d) => {
        const k = ymd(d);
        return (
          <div key={k} className="relative border-l border-[#1f1f1f]" data-testid={`cal-col-${k}`}>
            {HOURS.map((h) => <div key={h} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e.dataTransfer.getData("job"), k, h)} className="h-12 border-t border-[#1f1f1f]" data-testid={`cal-slot-${k}-${h}`} />)}
            {jobs.filter((j) => onDay(j, k)).map((j) => {
              const s = parseLocal(j.start_at), e = parseLocal(j.end_at) || new Date(s.getTime() + 36e5);
              const dayStart = new Date(`${k}T06:00:00`), dayEnd = new Date(`${k}T21:00:00`);
              const top = Math.max(0, (Math.max(s, dayStart) - dayStart) / 36e5) * 48;
              const h = Math.max(22, ((Math.min(e, dayEnd) - Math.max(s, dayStart)) / 36e5) * 48);
              return (
                <div key={j.id} className="absolute left-1 right-1" style={{ top, height: h }}>
                  <div draggable onDragStart={(ev) => ev.dataTransfer.setData("job", j.id)} onClick={() => onOpen(j)} data-testid={`cal-block-${j.id}`}
                    className="h-full cursor-pointer overflow-hidden rounded-md p-1.5 text-[11px] font-bold leading-tight text-black"
                    style={{ background: j.technician_ids?.length ? techColor(techs, j.technician_ids[0]) : "#9A9A9A" }}>
                    {fmtTime(j.start_at)} · {j.customer_name}<br /><span className="font-normal">{j.title}</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  </div>
);

export default function BillingCalendar() {
  const [st] = useBillingStatus();
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState({});
  const [open, setOpen] = useState(null);
  const [techFilter, setTechFilter] = useState("all");
  const [from, to] = useMemo(() => range(view, cursor), [view, cursor]);
  const load = useCallback(() => api(`/billing/schedule?start=${ymd(from)}&end=${ymd(to)}`).then(setData).catch((e) => toast.error(e.message)), [from, to]);
  useEffect(() => { if (st?.enabled) load(); }, [st, load]);
  useEffect(() => { api("/billing/customers").then((r) => setCustomers(Object.fromEntries(r.map((c) => [c.id, c])))).catch(() => {}); }, []);
  if (!st || (st.enabled && !data)) return <Spinner />;
  if (!st.enabled) return <NotEnabled />;
  const all = [...data.jobs, ...data.unscheduled];
  const jobs = data.jobs.filter((j) => techFilter === "all" || (j.technician_ids || []).includes(techFilter));
  const days = Array.from({ length: Math.round((to - from) / DAY) + 1 }, (_, i) => addDays(from, i));
  const move = (n) => setCursor(view === "month" ? new Date(cursor.getFullYear(), cursor.getMonth() + n, 1) : addDays(cursor, n * (view === "week" ? 7 : 1)));
  const drop = async (jid, day, hour) => {
    const j = all.find((x) => x.id === jid);
    if (!j) return;
    const s = parseLocal(j.start_at), e = parseLocal(j.end_at);
    const dur = s && e ? e - s : 2 * 36e5;
    const ns = new Date(`${day}T${hour != null ? String(hour).padStart(2, "0") : j.start_at?.slice(11, 13) || "09"}:${hour != null ? "00" : j.start_at?.slice(14, 16) || "00"}:00`);
    try {
      await api(`/billing/jobs/${jid}/schedule`, { method: "PATCH", body: { start_at: toLocal(ns), end_at: toLocal(new Date(ns.getTime() + dur)), technician_ids: j.technician_ids || [], remind_customer: !!j.remind_customer, notify_customer: false } });
      toast.success(`Moved to ${ns.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}. Open the job to email the customer the new time.`);
      load();
    } catch (err) { toast.error(err.message); }
  };
  const label = view === "month" ? cursor.toLocaleDateString([], { month: "long", year: "numeric" }) : view === "week" ? `${from.toLocaleDateString([], { month: "short", day: "numeric" })} – ${to.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}` : cursor.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  return (
    <PortalPage eyebrow="SCHEDULE" title="Calendar" back="/billing" width="max-w-7xl" testId="billing-calendar">
      <PracticeBanner st={st} />
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => move(-1)} className="rounded-lg border border-[#333] p-2" data-testid="cal-prev"><ChevronLeft size={16} /></button>
        <button onClick={() => setCursor(new Date())} className="rounded-lg border border-[#333] px-3 py-2 text-xs font-bold" data-testid="cal-today">TODAY</button>
        <button onClick={() => move(1)} className="rounded-lg border border-[#333] p-2" data-testid="cal-next"><ChevronRight size={16} /></button>
        <p className="min-w-[200px] flex-1 text-lg font-extrabold" data-testid="cal-label">{label}</p>
        <select value={techFilter} onChange={(e) => setTechFilter(e.target.value)} className="rounded-lg border border-[#333] bg-[#121212] px-3 py-2 text-xs" data-testid="cal-tech-filter">
          <option value="all">All technicians</option>{data.technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div className="flex overflow-hidden rounded-lg border border-[#333]">
          {["month", "week", "day"].map((v) => <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`} className={`px-3 py-2 text-xs font-bold ${view === v ? "bg-[var(--accent)] text-black" : "text-[#ccc]"}`}>{v.toUpperCase()}</button>)}
        </div>
      </div>
      <div className={`grid items-start gap-4 ${view === "week" ? "" : "lg:grid-cols-[1fr_240px]"}`}>
        {view === "month" ? <MonthGrid days={days} cursor={cursor} jobs={jobs} techs={data.technicians} onOpen={setOpen} onDrop={drop} />
          : <TimeGrid days={days} jobs={jobs} techs={data.technicians} onOpen={setOpen} onDrop={drop} />}
        <Card className="alr-scroll h-fit max-h-[70vh] space-y-2 overflow-y-auto" data-testid="cal-unscheduled">
          <p className="text-[10px] font-bold tracking-[2px] text-[#888]">UNSCHEDULED JOBS</p>
          <p className="text-[11px] text-[#777]">Drag a job onto a day, or tap it to set the time and technician.</p>
          {!data.unscheduled.length ? <p className="text-xs text-[#666]" data-testid="cal-unscheduled-empty">Nothing waiting.</p>
            : data.unscheduled.map((j) => <div key={j.id} draggable onDragStart={(e) => e.dataTransfer.setData("job", j.id)} onClick={() => setOpen(j)} data-testid={`cal-unsched-${j.id}`} className="cursor-grab rounded-lg border border-[#333] p-2 text-xs hover:border-[var(--accent)]"><b>#{j.number} {j.customer_name}</b><br /><span className="text-[#999]">{j.title}</span></div>)}
          <div className="space-y-1 border-t border-[#262626] pt-2">
            {data.technicians.map((t) => <p key={t.id} className="flex items-center gap-2 text-[11px] text-[#ccc]"><span className="h-2.5 w-2.5 rounded-full" style={{ background: techColor(data.technicians, t.id) }} />{t.name}</p>)}
          </div>
        </Card>
      </div>
      <ScheduleDialog job={open} techs={data.technicians} customers={customers} onClose={() => setOpen(null)} onSaved={load} />
    </PortalPage>
  );
}
